/**
 * Social Media API response entity types.
 *
 * `types.gen.ts` is auto-generated by openapi-typescript from
 * http://localhost:8375/openapi.json and provides typed path/operation/
 * request-body schemas (67 Pydantic request models as of 2026-04-03).
 *
 * The response-entity interfaces below (Account, Post, Schedule, etc.) are
 * NOT in the OpenAPI component schemas because the server wraps all responses
 * in a generic ApiResponse envelope (data: unknown).  They remain hand-
 * maintained here and are validated against the server via
 * backend/config/governance/api_schema_contracts.yaml.
 *
 * Usage:
 *   import type { Account, Post }               from "../types/social";
 *   import type { components }                  from "../types/types.gen";
 *   type PostCreate = components["schemas"]["PostCreate"];
 */

// Imported from the leaf module (not ./analytics) to avoid a circular import —
// analytics.ts itself imports AnalyticsOverview from this file. See
// analyticsShared.ts for the extraction rationale (E10-001, 2026-10-06).
import type { StoriesSummary } from "./analyticsShared";

export type Platform =
  | "facebook"
  | "instagram"
  | "google_business"
  | "linkedin"
  | "tiktok"
  | "pinterest"
  | "threads"
  | "bluesky"
  | "youtube";

// PostStatus, AccountStatus, and ApprovalStatus are canonical in @plerity/contracts/social.
// Import them for use within this file, then re-export with the public names so
// existing import paths within this app continue to work.
import type { PostStatus, SocialAccountStatus, SocialApprovalStatus } from "@plerity/contracts/social";

// Local aliases for use within this file's interface/type declarations
type AccountStatus = SocialAccountStatus;
type ApprovalStatus = SocialApprovalStatus;

// Re-export with the legacy names so existing import paths within this app work
export type { PostStatus };
export type { SocialAccountStatus as AccountStatus };
export type { SocialApprovalStatus as ApprovalStatus };

export interface Account {
  id: string;
  org_id: string;
  /** The client brand this account is associated with (agency multi-client
   * model). null = agency/org-level, unassigned to a specific client. */
  client_brand_id?: string | null;
  platform: Platform;
  account_name: string;
  account_id_external: string;
  profile_url: string | null;
  profile_image_url: string | null;
  follower_count: number;
  status: AccountStatus;
  token_expires_at: string | null;
  last_health_check_at: string | null;
  last_error: string | null;
  scopes: string[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** One Pinterest board — GET /api/accounts/{id}/pinterest/boards. Feeds the
 * composer board picker; the choice persists as posts.metadata.pinterest_board_id. */
export interface PinterestBoard {
  id: string;
  name: string;
  privacy: string | null;
  pin_count: number | null;
}

/** Where a post's audio/music came from — `audio_provenance` (computed server-side). */
export type AudioProvider =
  | "epidemic_sound"
  | "ai_generated"
  | "procedural"
  | "licensed_other"
  | "original_audio"
  | "none"
  | "instagram_library"
  | "instagram_original_sound";

export interface AudioProvenanceSource {
  provider: AudioProvider;
  /** e.g. "Epidemic Sound", "Instagram audio", "AI-generated", "Original audio". */
  provider_label: string;
  title: string | null;
  artist: string | null;
  license: string | null;
  /** `baked` = mixed into the video file; `attached_at_publish` = added by the network at publish. */
  mode: "baked" | "attached_at_publish";
  track_id: string | null;
  /** Short machine-ish note on where the server found this, e.g. "music ledger". */
  evidence: string;
}

export interface AudioProvenance {
  status: "known" | "none" | "unknown";
  /** Server-authored one-liner, e.g. "Epidemic Sound · The Lone Traveler · baked into video". */
  summary: string;
  sources: AudioProvenanceSource[];
  warnings: string[];
}

export interface Post {
  id: string;
  org_id: string;
  content: string;
  media_urls: string[];
  media_types: string[];
  link_url: string | null;
  hashtags: string[];
  /** Internal campaign tags (migration 083). Never published; optional for older payloads. */
  tags?: string[];
  first_comment: string | null;
  status: PostStatus;
  author_id: string | null;
  template_id: string | null;
  /** Composer publish options + attribution — see `backend/servers/social/routers/posts.py`
   * `PostCreate.metadata`. Notable key: `title?: string | null` (2026-10-10) —
   * the composer's optional, display-only post title shown in post lists and
   * Approvals; never published to any network (distinct from `video_title`,
   * which IS published to Facebook). See `lib/postTitle.ts`. */
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Approval workflow fields (added by approvals router — present in all post responses)
  approval_status?: ApprovalStatus | null;
  approval_notes?: string | null;
  approved_by?: number | null;
  approved_at?: string | null;
  // Aggregated by GET /api/posts + /api/posts/{id}: target networks and the
  // earliest active schedule (posts router).
  platforms?: Platform[];
  scheduled_at?: string | null;
  client_brand_id?: string | null;
  // The design this post was made from (metadata.design_id /
  // metadata.autopilot.design_id / designs.post_id), derived server-side on
  // GET /api/posts, /api/posts/{id} and /api/posts/pending-approval.
  linked_design_id?: string | null;
  // "When this will likely go out" absent a real schedule — derived
  // server-side on GET /api/posts/{id} (falls back to the legacy
  // metadata.autopilot.planned_at for older Autopilot drafts, so the client
  // only ever needs to read these three fields). Additive 2026-10-06.
  proposed_publish_at?: string | null;
  /** IANA zone the proposal was made in; null falls back to the browser's zone. */
  proposed_timezone?: string | null;
  /** Target networks for the proposal. */
  proposed_platforms?: string[];
  /** Where the post's music came from — computed on GET /api/posts/pending-approval and
   * GET /api/posts/{id}. ABSENT/null on older API responses: render nothing (not "unknown"). */
  audio_provenance?: AudioProvenance | null;
  /** The Instagram Story that goes out with this feed/Reel post (2026-10-16) — on
   * GET /api/posts/{id} and GET /api/calendar items. `null` = no Story; ABSENT on an
   * older API (render nothing). A Story itself carries `metadata.instagram_format = "story"`. */
  instagram_story_companion?: InstagramStoryCompanion | null;
  /** True only while the post sits, undecided, on an active client review link
   * (`services/client_review_gate.py`). `pending_approval` + false = the agency's
   * internal queue. null/absent = not computed on this response. */
  awaiting_client_review?: boolean | null;
  /** Engine render this post's video came from (`metadata.render_job_id`); null when no video. */
  render_job_id?: string | null;
  /** Video project an approver can open for timecoded comments (`metadata.video_project_id`). */
  video_project_id?: number | null;
  /** What kind of post this is (`services/content_format.py`, 2026-10-17) — on GET /api/posts,
   * /api/posts/{id}, /api/posts/pending-approval and /api/calendar items. ABSENT on an older API. */
  content_format?: ContentFormat;
  /** `content_format === "story"`. Use THIS (not `storyState()`) on list rows: list endpoints send
   * `instagram_story_companion: null` without computing it. ABSENT on an older API. */
  is_story?: boolean;
  /** The feed/Reel post id a Story was created for; null = a standalone Story (or not a Story). */
  story_companion_of?: string | null;
  /** Daily Stories lane (`services/story_assist.py`, 2026-10-17): `api` = Graph auto-publish,
   * `assisted` = a person posts it from the iOS app with real stickers. null/absent = not a Story
   * (or an older API). */
  story_lane?: StoryLane | null;
  /** Stickers the person adds by hand (assisted lane). Empty on an older API. */
  story_sticker_plan?: StickerPlanItem[];
  /** Frame `index` of `count` in a multi-frame Story run. */
  story_sequence?: StorySequence | null;
  /** Id of the post this Story answers (a question-sticker reply Story). */
  story_answer_of?: string | null;
  /** Graph `user_tags` of an API-lane Story. */
  story_user_tags?: StoryUserTag[];
  /** Assisted Story that is also auto-posted to Facebook. */
  story_fb_auto?: boolean;
  /** Calendar items only: `scheduled` | `published` | `failed` | `awaiting_approval` | `native`. */
  calendar_kind?: string | null;
  /** Calendar items only: an Instagram target is parked for a person to post (exact, server-side). */
  awaiting_manual_post?: boolean;
}

export type StoryLane = "api" | "assisted";

export type StickerType =
  | "poll"
  | "question"
  | "quiz"
  | "slider"
  | "countdown"
  | "link"
  | "location"
  | "mention"
  | "music";

/** Mirrors `schemas/story_assist.py::StickerPlanItem`. */
export interface StickerPlanItem {
  type: StickerType;
  prompt?: string | null;
  options?: string[];
  url?: string | null;
  label?: string | null;
  placement_hint?: { x: number; y: number } | null;
  required?: boolean;
  /** Quiz only: 0-based index of the right answer in `options`. */
  correct_index?: number | null;
}

/** Mirrors `schemas/story_assist.py::StorySequence`. */
export interface StorySequence {
  id: string;
  index: number;
  count: number;
}

/** Mirrors `schemas/story_assist.py::StoryUserTag`. */
export interface StoryUserTag {
  username: string;
  x?: number | null;
  y?: number | null;
}

/** Mirrors `services/content_format.py::ContentFormat`. */
export type ContentFormat = "story" | "reel" | "carousel" | "image" | "video" | "text";

/** `GET /api/posts/approval-counts` (`schemas/approvals.py::ApprovalCountsData`). Status counts are
 * numbers; `by_format` (the pending_approval queue per format) and `by_format_by_status` are additive
 * 2026-10-17 and ABSENT on an older API. */
export interface ApprovalCounts {
  draft?: number;
  pending_approval?: number;
  approved?: number;
  rejected?: number;
  missing_media?: number;
  stories_pending?: number;
  /** Approved posts that were then edited and are back waiting for re-approval (2026-10-21). */
  edited_after_approval?: number;
  by_format?: Partial<Record<ContentFormat, number>>;
  by_format_by_status?: Record<string, Partial<Record<ContentFormat, number>>>;
  [key: string]:
    | number
    | Partial<Record<ContentFormat, number>>
    | Record<string, Partial<Record<ContentFormat, number>>>
    | undefined;
}

/** The `content_format` query value the Calendar / Approvals filters use (`feed` = not a Story). */
export type StoryFormatFilter = "all" | "feed" | "story";

export type StoryCompanionStatus = "scheduled" | "publishing" | "published" | "failed" | "awaiting_approval";

/** Mirrors `backend/servers/social/schemas/story_companion.py::InstagramStoryCompanion`. */
export interface InstagramStoryCompanion {
  post_id: string;
  /** The Story's own time: its pending schedule, else when it posted, else its proposed time. */
  scheduled_at: string | null;
  status: StoryCompanionStatus;
  /** True when the Story was created FOR this post (`metadata.story_companion_of`). */
  linked: boolean;
}

/** Compact recurring-series badge on calendar rows (`schemas/series.py::SeriesRef`). */
export interface SeriesRef {
  id: string;
  name: string;
  episode_no?: number | null;
}

export interface CalendarPost extends Post {
  scheduled_at: string | null;
  timezone: string | null;
  platforms: Platform[];
  /** Recurring series this post is an episode of (2026-10-19). `null` = not a series post;
   * ABSENT on an older API — render no badge either way. */
  series?: SeriesRef | null;
}

export interface Schedule {
  id: string;
  post_id: string;
  scheduled_at: string;
  timezone: string;
  recurrence: string | null;
  recurrence_end_at: string | null;
  status: "pending" | "processing" | "completed" | "failed" | "cancelled";
  attempts: number;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Comment {
  id: string;
  account_id: string;
  post_platform_id: string | null;
  platform: Platform;
  platform_comment_id: string | null;
  parent_comment_id: string | null;
  author_name: string | null;
  author_id_external: string | null;
  author_profile_url: string | null;
  content: string;
  sentiment: string | null;
  is_reply: boolean;
  replied: boolean;
  reply_content: string | null;
  replied_at: string | null;
  is_hidden: boolean;
  metadata: Record<string, unknown>;
  posted_at: string;
  created_at: string;
  // ── Inbox context (GET /api/inbox — backend/servers/social/schemas/inbox.py).
  // Optional: only the inbox list/thread responses carry them.
  account_name?: string | null;
  account_platform?: string | null;
  /** AI-drafted reply; a DRAFT only — never sent automatically. */
  suggested_reply?: SuggestedReply | null;
  // Triage (migration 030)
  done_at?: string | null;
  done_by_user_id?: number | null;
  assigned_to_user_id?: number | null;
  assigned_to_name?: string | null;
  assigned_at?: string | null;
  platform_reply_id?: string | null;
  // Context joined by the inbox query
  client_brand_id?: string | null;
  client_brand_name?: string | null;
  /** True when the author is the connected account itself (thread views only). */
  is_own?: boolean;
  post_caption?: string | null;
  post_thumbnail_url?: string | null;
  post_permalink?: string | null;
  post_media_type?: "image" | "video" | "reel" | "carousel" | "story" | "text" | null;
  /** `social_media.posts.id` when the comment is on a post we published. */
  post_id?: string | null;
  post_posted_at?: string | null;
  note_count?: number;
  /** Classifier intent bucket (`metadata.classification.category`). */
  category?: string | null;
  // Smart triage (migration 052); null = not triaged yet.
  triage?: "needs_reply" | "review" | "no_reply_needed" | null;
  triage_reason?: string | null;
  triage_source?: "rule" | "llm" | "human" | null;
  triaged_at?: string | null;
}

/** AI-drafted SM-006 reply suggestion (`schemas/inbox.py::SuggestedReply`). Re-exported by `api/queries.ts`. */
export interface SuggestedReply {
  text: string;
  confidence: number | null;
  category: string | null;
  source: string | null;
  generated_at: string | null;
  crm_company_id: number | null;
}

/** Media kind carried through the create-flow composer + previews. */
export type MediaKind = "image" | "video";

/**
 * Lightweight media descriptor used by the create-flow composer, the attached-
 * media strip, and the platform preview. Distinct from the richer `MediaItem`
 * API entity below — the composer only needs the persistent URL plus whether it
 * is an image or a video (so a video renders a `<video>` frame, not a broken
 * `<img>`). Structurally identical to `UploadedMedia` from `MediaUploader`, so
 * an uploader result flows straight into the flow store.
 */
export interface AttachedMedia {
  url: string;
  type: MediaKind;
  /** DAM `client_assets.assets` id when picked from the Client assets library (series attach sends it as `asset_ids`). */
  assetId?: number;
}

/** Infer whether a bare URL points at a video (extension or `data:video/…`).
 *  Used only as a fallback when a caller supplies URLs without a known type. */
export function isVideoUrl(url: string): boolean {
  if (url.startsWith("data:video/")) return true;
  const clean = url.split(/[?#]/)[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v|ogv|avi|mkv)$/.test(clean);
}

export interface MediaItem {
  id: string;
  org_id: string;
  filename: string;
  url: string;
  thumbnail_url: string | null;
  media_type: "image" | "video" | "gif";
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  alt_text: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface AnalyticsSnapshot {
  id: string;
  account_id: string;
  /** Additive 2026-10-01 — `/api/analytics/platform/{platform}` joins it in. */
  account_name?: string | null;
  org_id: string;
  snapshot_date: string;
  followers: number;
  following: number;
  posts_count: number;
  reach: number;
  impressions: number;
  engagement: number;
  engagement_rate: number;
  profile_views: number;
  website_clicks: number;
  phone_calls: number;
  direction_requests: number;
  metrics_raw: Record<string, unknown>;
  created_at: string;
  /** Account-level daily views (IG `views` insight; migration 045). null = not
   * collected. Additive 2026-10-03. */
  views?: number | null;
}

interface AnalyticsPlatformSummary {
  platform: Platform;
  account_name: string;
  followers: number;
  reach: number;
  impressions: number;
  engagement: number;
  /** engagement ÷ reach as a FRACTION (0.05 = 5%). */
  engagement_rate: number;
  /** Additive 2026-10-01 — false when the platform shared no reach in the window
   * (e.g. FB Page token without read_insights); render the rate as "—". */
  reach_available?: boolean | null;
  /** Additive 2026-10-03 — views on posts published in the window (post
   * archive); null = not reported by the platform. */
  views?: number | null;
  prior_views?: number | null;
  views_available?: boolean | null;
  /** Additive 2026-10-03 — this account's Instagram Stories in the window; null for
   * non-IG accounts and IG accounts with no archived story. */
  stories?: StoriesSummary | null;
}

interface AnalyticsTotal {
  followers: number;
  reach: number;
  impressions: number;
  engagement: number;
  /** Additive 2026-10-03 — views on posts published in the window; null = none reported. */
  views?: number | null;
}

export interface AnalyticsOverview {
  total: AnalyticsTotal;
  platforms: AnalyticsPlatformSummary[];
  /** Additive 2026-10-01 — full typing in types/analytics.ts (AnalyticsOverviewV2). */
  previous?: AnalyticsTotal | null;
  previous_period?: { start: string; end: string } | null;
  reach_coverage?: { accounts_with_reach: number; accounts_total: number };
  /** Additive 2026-10-03 — Instagram Stories posted in the window; null when none archived. */
  stories?: StoriesSummary | null;
}

// ── Churn Risk ───────────────────────────────────────────────────────────────

/** Deterministic risk ladder computed server-side — see
 * backend/servers/social/routers/analytics.py::get_churn_risk. */
export type ChurnRiskLevel = "none" | "low" | "medium" | "high" | "critical";

export interface ChurnRiskItem {
  client_brand_id: string;
  brand_name: string;
  brand_slug: string;
  has_connected_account: boolean;
  active_account_count: number;
  last_approval_action_at: string | null;
  last_authored_at: string | null;
  last_published_at: string | null;
  last_scheduled_at: string | null;
  last_activity_at: string | null;
  days_since_activity: number | null;
  posts_last_30d: number;
  approvals_last_30d: number;
  risk_level: ChurnRiskLevel;
  risk_reasons: string[];
  /** `client_brands.metadata.delivery_mode` — "off_platform" marks a legacy
   * client served outside this product (never flagged from platform signals). */
  delivery_mode?: string | null;
  /** Active connected accounts (same value as `active_account_count`). */
  connected_accounts: number;
  /** Platform publishes in the last 21 days. */
  published_last_21d: number;
  /** Pending (queued, not stale) schedules. */
  scheduled_pending: number;
  days_since_last_publish: number | null;
  /** Connected (>=1 active account) but 0 publishes in 21d and nothing
   * scheduled — raises risk to at least "high". Never true off-platform. */
  dormant_connected: boolean;
}

/** `GET /api/analytics/churn-risk` response payload — org-wide (cross-client),
 * never scoped to the selected `client_brand_id`. */
export interface ChurnRiskData {
  items: ChurnRiskItem[];
  generated_at: string;
}

// ── Day-0 → Day-14 onboarding cadence (retention memo lever L1, SOP SM-061) ──

/** Deterministic onboarding status — see
 * backend/servers/social/services/onboarding_cadence.py. */
export type OnboardingStatus = "on_track" | "at_risk" | "overdue" | "complete" | "legacy" | "not_applicable";

/** First unmet critical-path step (null once published / not applicable). */
export type OnboardingStep = "connect" | "kit" | "draft" | "approval" | "publish";

export interface OnboardingItem {
  client_brand_id: string;
  brand_name: string;
  brand_slug: string;
  created_at: string;
  /** Whole days since provisioning (day 0 = provisioned today). */
  day: number;
  delivery_mode?: string | null;
  first_account_connected_at: string | null;
  active_accounts: number;
  /** First kit with real colors/logos (approximate — last kit edit time). */
  kit_ready_at: string | null;
  first_draft_at: string | null;
  first_approval_at: string | null;
  first_published_at: string | null;
  scheduled_pending: number;
  days_to_first_publish: number | null;
  published_on_time: boolean | null;
  status: OnboardingStatus;
  blocking_step: OnboardingStep | null;
  next_action: string;
}

export interface OnboardingSummary {
  brands_in_window: number;
  on_track: number;
  at_risk: number;
  overdue: number;
  complete: number;
  legacy: number;
  not_applicable: number;
  median_days_to_first_publish: number | null;
}

/** `GET /api/analytics/onboarding` response payload — org-wide (cross-client). */
export interface OnboardingData {
  items: OnboardingItem[];
  summary: OnboardingSummary;
  window_days: number;
  generated_at: string;
}

// ── Monthly client report (retention memo lever L2) ─────────────────────────

/** Outcome fields shared by a platform row and the brand totals. */
interface MonthlyReportMetrics {
  followers_start: number;
  followers_end: number;
  follower_delta: number;
  follower_delta_pct: number | null;
  reach: number;
  engagement: number;
  prior_reach: number;
  prior_engagement: number;
  reach_delta_pct: number | null;
  engagement_delta_pct: number | null;
  posts_published: number;
  /** Engagement earned by posts published in the month (post-level analytics). */
  post_engagement: number;
  profile_views: number;
  website_clicks: number;
  /** false → reach is 0 for the whole window: render "not reported by platform". */
  reach_available: boolean;
  /** Views on posts published in the month (IG/FB `views`, TikTok plays — from the
   * post archive). null = not reported by the platform; NEVER render as 0.
   * Additive 2026-10-03. */
  views: number | null;
  /** Same metric for the prior period; null when there is no baseline. */
  prior_views: number | null;
  views_delta_pct: number | null;
  /** false → no post in the month carried a view count: render "not reported". */
  views_available: boolean;
  /** Instagram Stories posted in the month vs the prior window (additive 2026-10-03).
   * null for non-Instagram rows / brands with no archived story in either window. */
  stories?: MonthlyReportStories | null;
  /** true only when at least one story was POSTED this month; false → "No stories this month". */
  stories_available?: boolean;
}

/** Instagram Stories block of the monthly report (`MonthlyReportStories`). Stories are
 * NOT posts. Every metric is null when Instagram did not report it; every `prior_*`
 * is null without a prior-window baseline; deltas need both. */
export interface MonthlyReportStories {
  count: number;
  views: number | null;
  reach: number | null;
  link_clicks: number | null;
  replies: number | null;
  prior_count: number | null;
  prior_views: number | null;
  prior_reach: number | null;
  prior_link_clicks: number | null;
  prior_replies: number | null;
  count_delta_pct: number | null;
  views_delta_pct: number | null;
  reach_delta_pct: number | null;
  link_clicks_delta_pct: number | null;
  replies_delta_pct: number | null;
}

export interface MonthlyReportPlatformRow extends MonthlyReportMetrics {
  platform: string;
  account_name: string;
}

export type MonthlyReportTotals = MonthlyReportMetrics;

export interface MonthlyReportTopPost {
  post_platform_id: string;
  post_id: string | null;
  platform: string;
  account_name: string | null;
  published_at: string | null;
  excerpt: string;
  media_url: string | null;
  platform_post_id: string | null;
  /** Deep link to the live post (archive rows). Additive 2026-10-01. */
  permalink?: string | null;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  reach: number | null;
  /** Views on the post (archive rows; null = not reported). Additive 2026-10-03. */
  views?: number | null;
  engagement: number;
}

export interface MonthlyReportCommunity {
  comments_received: number;
  comments_replied: number;
  reply_rate_pct: number | null;
  avg_reply_hours: number | null;
  positive_pct: number | null;
  thread_replies: number;
}

/** `GET /api/analytics/monthly-report` payload — one client brand, one month.
 * Outcomes only: impressions are deliberately never reported. */
export interface MonthlyReportData {
  brand: { client_brand_id: string; brand_name: string; brand_slug: string };
  period: { month: string; start: string; end: string };
  prior_period: { start: string; end: string };
  generated_at: string;
  platforms: MonthlyReportPlatformRow[];
  totals: MonthlyReportTotals;
  top_posts: MonthlyReportTopPost[];
  community: MonthlyReportCommunity;
  highlights: string[];
}

export interface Template {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  content: string;
  media_urls: string[];
  hashtags: string[];
  platforms: Platform[];
  category: string;
  is_global: boolean;
  usage_count: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  /** Client brand the template is scoped to; null = org-wide / global. */
  client_brand_id?: string | null;
}

// ── Copywriter ───────────────────────────────────────────────────────────────

export type CopywriterProvider = "ollama" | "openai" | "anthropic";

export interface CopywriterConfig {
  org_id: number;
  provider: CopywriterProvider;
  model: string;
  temperature: number;
  max_variants: number;
  auto_hashtags: boolean;
  auto_emoji: boolean;
  tone_override: string | null;
  content_guidelines: string | null;
  enabled: boolean;
  updated_at: string | null;
}

// ── User Settings ────────────────────────────────────────────────────────────
//
// Backs /social/settings page. Per-user, per-org. Stored as JSONB on
// social_media.user_settings (composite PK org_id + user_id).
// API contract: GET/PUT /api/user-settings → ApiResponse<UserSettings>.

export type DefaultPostStatus = "draft" | "scheduled";

interface UserSettingsGeneral {
  /** IANA timezone name; null = client-detected fallback. */
  timezone: string | null;
  default_post_status: DefaultPostStatus | string;
}

interface UserSettingsNotifications {
  post_published: boolean;
  post_failed: boolean;
  token_expiring: boolean;
  // Notification backbone (lane A2, 2026-10-04) — optional so an API that
  // predates them still type-checks; the Settings page applies the defaults.
  approval_requested?: boolean;
  approval_decision?: boolean;
  comments_digest?: boolean;
  reviews_digest?: boolean;
  render_finished?: boolean;
  listening_alert?: boolean;
  account_connected?: boolean;
  report_delivery_failed?: boolean;
  dm_attention?: boolean;
  video_autopilot_blocked?: boolean;
  video_drafts_ready?: boolean;
  content_library_pending?: boolean;
  content_library_digest?: boolean;
  story_manual_due?: boolean;
  push_enabled?: boolean;
}

interface UserSettingsPayload {
  general: UserSettingsGeneral;
  notifications: UserSettingsNotifications;
}

export interface UserSettings {
  org_id: number;
  user_id: number;
  settings: UserSettingsPayload;
  updated_at: string | null;
}

/** Partial-merge body for PUT /api/user-settings — both sections optional. */
export interface UserSettingsUpdate {
  general?: Partial<UserSettingsGeneral>;
  notifications?: Partial<UserSettingsNotifications>;
}

// ── Org Settings ────────────────────────────────────────────────────────────
//
// Org-wide defaults shared by every user of the org — contrast with the
// per-user UserSettings above. Stored as JSONB on social_media.org_settings
// (PK org_id). API contract: GET/PUT /api/org-settings → ApiResponse<OrgSettings>.

export interface OrgUtmDefaults {
  /** Pre-fills a brand-new post's UTM toggle to ON for every user in this org. */
  enabled: boolean;
  /** Default utm_campaign slug; "" falls back to a caption-derived campaign. */
  campaign: string;
}

interface OrgSettingsPayload {
  utm_defaults: OrgUtmDefaults;
}

export interface OrgSettings {
  org_id: number;
  settings: OrgSettingsPayload;
  updated_at: string | null;
}

/** Partial-merge body for PUT /api/org-settings. */
export interface OrgSettingsUpdate {
  utm_defaults?: Partial<OrgUtmDefaults>;
}

// ── Knowledge Base ──────────────────────────────────────────────────────────

export type KnowledgeChunkType = "brand_voice" | "guidelines" | "industry";

export interface KnowledgeChunk {
  id: string;
  chunk_type: KnowledgeChunkType;
  content: string;
  source: string | null;
  account_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export interface KnowledgeStats {
  total_chunks: number;
  by_type: Record<string, number>;
  by_account: Array<{ account_id: string | null; account_name: string | null; count: number }>;
  last_updated: string | null;
  pgvector_available: boolean;
}

export interface KnowledgeChunkList {
  chunks: KnowledgeChunk[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface KnowledgeSearchResult {
  id: string;
  chunk_type: string;
  content: string;
  source: string | null;
  account_id: string | null;
  similarity: number;
  created_at: string | null;
}

// ── Brand Voice ─────────────────────────────────────────────────────────────

export interface BrandVoiceProfile {
  personality_traits: string[];
  tone_spectrum: Record<string, number>;
  content_themes: string[];
  writing_style: string;
  top_hashtags: Array<{ tag: string; count: number }>;
  emoji_usage: {
    total_emojis: number;
    per_post_avg: number;
    frequency: "low" | "medium" | "high";
  };
  content_patterns: {
    avg_caption_length: number;
    avg_word_count: number;
    avg_hashtags_per_post: number;
    total_unique_hashtags: number;
    posts_with_content: number;
  };
  performance_insights: string[];
  stats: Record<string, unknown>;
  analyzed_at: string;
  post_count_analyzed: number;
}

export interface CompetitorAccount {
  id: string;
  org_id: number;
  account_id: string | null;
  competitor_instagram_username: string;
  competitor_name: string | null;
  last_analyzed_at: string | null;
  created_at: string;
}

export interface CompetitorInsights {
  competitor: string;
  competitor_stats: Record<string, unknown>;
  brand_stats: Record<string, unknown>;
  insights: string[];
  content_gaps: string[];
  hashtag_comparison: {
    brand_unique?: string[];
    competitor_unique?: string[];
    shared?: string[];
  };
  analyzed_at: string;
}

// ── Post Ingestion ──────────────────────────────────────────────────────────

export interface IngestionOverview {
  sources: Record<string, { count: number; last_at: string | null }>;
  available_internal_posts: number;
  available_archive_posts: number;
  total_chunks: number;
}

// ── Document Upload ─────────────────────────────────────────────────────────

export interface DocumentEntry {
  source: string;
  chunk_count: number;
  chunk_types: string[];
  first_ingested: string | null;
  last_ingested: string | null;
}

// ── Knowledge Health ────────────────────────────────────────────────────────

export interface KnowledgeHealth {
  total_chunks: number;
  by_type: Record<string, number>;
  by_source: Record<string, number>;
  freshness: {
    fresh_7d: number;
    recent_30d: number;
    aging_90d: number;
    stale_90d_plus: number;
    score: number;
  };
  quality: {
    with_embeddings: number;
    without_embeddings: number;
    embedding_coverage_pct: number;
    avg_content_length: number;
  };
  last_ingestion: string | null;
  /** Whether the pgvector extension is enabled — semantic (RAG) search live. */
  pgvector_available?: boolean;
  health_score: number;
}

export interface QualityReport {
  total_chunks: number;
  short_chunks: number;
  long_chunks: number;
  type_diversity: number;
  source_diversity: number;
  warnings: string[];
  quality_score: number;
}

// ── Content Generation ───────────────────────────────────────────────────────

export interface ContentVariant {
  text: string;
  hashtags: string[];
  cta: string;
  emoji_level: "none" | "low" | "medium" | "high";
  score: number;
  reasoning: string;
}

export interface GenerationResult {
  generation_id: string;
  prompt: string;
  platforms: string[];
  variants: Record<string, ContentVariant[]>;
  config: {
    provider: string;
    model: string;
    temperature: number;
  };
  created_at: string;
}

interface GenerationHistoryItem {
  id: string;
  prompt: string;
  platforms: string[];
  variant_count: number;
  created_at: string;
  feedback_rating: number | null;
}

export interface GenerationHistoryList {
  items: GenerationHistoryItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ── Copywriter Analytics ─────────────────────────────────────────────────────

export interface CopywriterAnalytics {
  total_generations: number;
  total_variants_generated: number;
  acceptance_rate: number;
  avg_feedback_rating: number | null;
  avg_engagement_improvement: number | null;
  generations_by_platform: Record<string, number>;
  generations_by_provider: Record<string, number>;
  top_performing_generations: TopGeneration[];
  weekly_trend: WeeklyTrend[];
}

export interface TopGeneration {
  id: string;
  prompt: string;
  platform: string | null;
  feedback_rating: number | null;
  engagement_score: number | null;
  improvement_pct: number | null;
  created_at: string | null;
}

interface WeeklyTrend {
  week: string;
  count: number;
  avg_rating: number | null;
}

export interface CopywriterInsight {
  text: string;
  category: string;
}

// ── Copywriter Chat ──────────────────────────────────────────────────────────

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

interface ChatSession {
  id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatSessionDetail extends ChatSession {
  messages: ChatMessage[];
  account_id: string | null;
}

export interface ChatSessionList {
  sessions: ChatSession[];
  total: number;
  page: number;
  page_size: number;
}

// ── Instagram Archive ────────────────────────────────────────────────────────

type ScrapeJobType = "graph_api" | "instaloader" | "competitor";
export type ScrapeJobStatus = "pending" | "running" | "completed" | "failed" | "cancelled";
/** `STORY` rows are archived Instagram Stories — listed only when `media_type=STORY` is requested. */
type MediaTypeFilter = "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM" | "STORY";

export interface ScrapeJob {
  id: number;
  org_id: number;
  account_id: string | null;
  username: string;
  job_type: ScrapeJobType;
  status: ScrapeJobStatus;
  total_posts: number | null;
  fetched_posts: number;
  archived_media: number;
  last_cursor: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  progress_pct: number;
}

export interface ArchivedPost {
  id: number;
  org_id: number;
  account_id: string | null;
  username: string;
  instagram_post_id: string;
  instagram_user_id: string | null;
  media_type: MediaTypeFilter;
  caption: string | null;
  permalink: string | null;
  posted_at: string | null;
  likes_count: number;
  comments_count: number;
  saves_count: number;
  shares_count: number;
  reach: number | null;
  /** Legacy — Meta deprecated per-media impressions for media after 2024-07-02. */
  impressions: number | null;
  /** Per-media `views` (FEED/REELS/STORY) — the truth column; display `views ?? impressions`. */
  views: number | null;
  engagement: number | null;
  /** Link clicks — STORY `link_clicks` (link-sticker taps); null when not reported. Additive 2026-10-03. */
  clicks?: number | null;
  media_urls: string[];
  r2_media_urls: string[];
  thumbnail_url: string | null;
  r2_thumbnail_url: string | null;
  hashtags: string[];
  mentions: string[];
  detected_urls: string[];
  location_name: string | null;
  is_video: boolean;
  children: Record<string, unknown>[] | null;
  raw_insights: Record<string, unknown> | null;
  source: "graph_api" | "instaloader" | "stories_api";
  created_at: string;
  updated_at: string;
}

export interface ArchivedPostList {
  items: ArchivedPost[];
  total: number;
  page: number;
  per_page: number;
}

export interface ArchiveStats {
  total_posts: number;
  by_media_type: Record<string, number>;
  by_month: Array<{ month: string; count: number }>;
  top_hashtags: Array<{ tag: string; count: number }>;
  /** Archived Instagram Stories (not counted in `total_posts`). Additive 2026-10-03; absent on an older API. */
  total_stories?: number;
}

// ── Design Analysis ─────────────────────────────────────────────────────────

interface ColorInfo {
  hex: string;
  pct: number;
  score: number;
}

export interface DesignInsights {
  total_analyzed: number;
  category_distribution: Record<string, number>;
  style_distribution: Record<string, number>;
  top_colors: ColorInfo[];
  text_overlay_pct: number;
  composition_distribution: Record<string, number>;
  avg_brand_score: number | null;
}

export interface BrandConsistency {
  score: number | null;
  posts_scored: number;
  brand_palette: string[];
}

export interface VisualEvolutionEntry {
  month: string;
  post_count: number;
  dominant_style: string | null;
  dominant_category: string | null;
  avg_brand_score: number | null;
  sample_colors: string[];
}

export interface VisualEvolution {
  entries: VisualEvolutionEntry[];
}

export interface AccountHealth {
  username: string;
  schedule_id: number;
  enabled: boolean;
  job_type: string;
  frequency: string;
  last_run_at: string | null;
  last_status: string | null;
  next_scheduled: string | null;
  consecutive_failures: number;
}

export interface FailureRecord {
  job_id: number;
  username: string;
  error_message: string | null;
  failed_at: string | null;
}

export interface MonitoringData {
  accounts: AccountHealth[];
  total_archived_posts: number;
  total_analyzed: number;
  storage_estimate_mb: number;
  recent_failures: FailureRecord[];
}

// ── Scrape Schedules ────────────────────────────────────────────────────────

export type ScheduleFrequency = "hourly" | "daily" | "weekly" | "monthly";

export interface ScrapeSchedule {
  id: number;
  org_id: number;
  username: string;
  account_id: string | null;
  job_type: ScrapeJobType;
  frequency: ScheduleFrequency;
  cron_expression: string | null;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  last_job_id: number | null;
  last_job_status: string | null;
  consecutive_failures: number;
  max_failures: number;
  created_at: string;
  updated_at: string;
}

export const FREQUENCY_LABELS: Record<ScheduleFrequency, string> = {
  hourly: "Every Hour",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

export const SCRAPE_STATUS_COLORS: Record<ScrapeJobStatus, string> = {
  pending: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  running: "bg-info-bg text-info-strong",
  completed: "bg-success-bg text-success-strong",
  failed: "bg-danger-bg text-danger-strong",
  cancelled: "bg-gray-100 text-muted dark:bg-gray-800",
};

export const MEDIA_TYPE_LABELS: Record<MediaTypeFilter, string> = {
  IMAGE: "Image",
  VIDEO: "Video",
  CAROUSEL_ALBUM: "Carousel",
  STORY: "Story",
};

// ── Hashtag Research ─────────────────────────────────────────────────────────

export interface HashtagSuggestion {
  tag: string;
  category: "broad" | "niche" | "branded" | "location";
  frequency: number;
  relevance: number;
}

export interface HashtagResearchResult {
  topic: string;
  platform: string;
  suggested_count: number;
  platform_limit: number;
  suggestions: HashtagSuggestion[];
  popular_in_org: Array<{ tag: string; count: number }>;
  co_occurring: Record<string, string[]>;
}

export interface PopularHashtag {
  tag: string;
  count: number;
  platforms: string[];
}

// ── Approval Workflow ──────────────────────────────────────────────────────

// ApprovalStatus (= SocialApprovalStatus) is re-exported at the top of this file.
// APPROVAL_STATUS_COLORS and APPROVAL_STATUS_LABELS removed — use SOCIAL_APPROVAL_STATUS_META
// from @plerity/contracts/social in components that need label + variant.

// ── Design Publishing ─────────────────────────────────────────────────────

export interface DesignPerformance {
  design_id: string;
  design_name: string;
  preset: string | null;
  thumbnail_data_url: string | null;
  design_created_at: string | null;
  times_used: number;
  avg_engagement_rate: number;
  total_impressions: number;
  total_engagement: number;
  platforms: string[];
}

export interface AIPerformanceData {
  ai: {
    count: number;
    avg_engagement_rate: number;
    avg_impressions: number;
    avg_likes: number;
    avg_comments: number;
    avg_shares: number;
  };
  manual: {
    count: number;
    avg_engagement_rate: number;
    avg_impressions: number;
    avg_likes: number;
    avg_comments: number;
    avg_shares: number;
  };
  improvement_pct: number;
}

export interface PendingApprovalList {
  items: Post[];
  total: number;
  page: number;
  page_size: number;
}

export interface ApprovalHistoryEntry {
  id: number;
  org_id: number;
  post_id: string;
  action: string;
  actor_email: string;
  actor_name: string;
  notes: string;
  created_at: string;
}

export interface TemplateRanking {
  template_id: string;
  template_name: string;
  template_category: string | null;
  preset: string | null;
  usage_count: number;
  thumbnail_data_url: string | null;
  designs_created: number;
  posts_created: number;
  avg_engagement_rate: number;
  total_impressions: number;
  total_engagement: number;
}

export interface DesignAnalyticsSummary {
  total_designs: number;
  total_templates: number;
  posts_with_designs: number;
  avg_engagement: number;
  total_impressions: number;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export const PLATFORM_LABELS: Record<Platform, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  google_business: "Google Business",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  threads: "Threads",
  bluesky: "Bluesky",
  youtube: "YouTube",
};

export const PLATFORM_COLORS: Record<Platform, string> = {
  facebook: "bg-brand-700 text-white",
  instagram: "bg-pink-500 text-white",
  google_business: "bg-green-600 text-white",
  linkedin: "bg-brand-700 text-white",
  tiktok: "bg-black text-white",
  pinterest: "bg-red-600 text-white",
  threads: "bg-black text-white dark:bg-gray-800",
  bluesky: "bg-sky-500 text-white dark:bg-sky-600",
  youtube: "bg-red-600 text-white",
};

// POST_STATUS_COLORS removed — use POST_STATUS_META from @plerity/contracts/social.
// ACCOUNT_STATUS_COLORS removed — use SOCIAL_ACCOUNT_STATUS_META from @plerity/contracts/social.
